<template>
  <div v-if="isShownPromoCode" class="promo-code-container">
    <toggle-panel
      v-model="promoCode.active"
      :title="$t('common.promocode')"
      @click="handleClickOnPanel"
    >
      <coupon-icon slot="icon" />
      <div slot="content" class="promo-code-content">
        <div class="input-container">
          <capusta-input
            v-model="promoCode.code"
            v-mask="promoCodeTokens"
            :label="$t('common.promocode')"
            :placeholder="$t('promocode.placeholder')"
            :error="!isValidPromoCode"
            :validation="validatePromoCode"
          />
        </div>
        <div class="input-container input-promo-code-container">
          <capusta-input
            v-if="isShownDiscountPercent"
            key="discountPercent"
            v-model="promoCode.discountPercent"
            v-mask="percentTokens"
            :label="$t('promocode.discount.label')"
            :placeholder="displayPlaceholderDiscountPercent"
            :error="!isValidDiscountPercent"
            :validation="validateDiscountPercent"
          >
            <div slot="composite" class="input-control-button" @click="onToggleInputType">
              {{ displayDiscountSymbol }}
            </div>
          </capusta-input>
          <capusta-input
            v-else
            key="discountAmount"
            v-model="promoCode.discountAmount"
            v-mask="['####', '# ###', '## ###']"
            :label="$t('promocode.discount.label')"
            :placeholder="displayPlaceholderDiscountAmount"
            :error="!isValidDiscountAmount"
            :validation="validateDiscountAmount"
          >
            <div slot="composite" class="input-control-button" @click="onToggleInputType">
              {{ displayDiscountSymbol }}
            </div>
          </capusta-input>
        </div>
        <div class="switcher-container switcher-link-container">
          <cs-switcher
            v-model="promoCode.applyToAllBills"
            :label="$t('promocode.apply.to.all.bills')"
          />
          <help-link source="https://help.capusta.space/payment_page/promo_code" />
        </div>
        <div class="button-primary-container">
          <cs-button :disabled="!isValid" width="100%" @click="onSubmit">
            {{ $t('common.save') }}
          </cs-button>
        </div>
      </div>
    </toggle-panel>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import { mask } from 'vue-the-mask';

import { formatAmount } from 'lib/utils/amount';

import HTTPService from '@/services/Http';

import CouponIcon from 'atoms/icons/shopping/Coupon';
import TogglePanel from 'molecules/panels/TogglePanel';
import CapustaInput from 'molecules/capusta-input/CapustaInput';
import HelpLink from 'molecules/HelpLink';

const MIN_LIMIT_AMOUNT = 0.01;

const DEFAULT_STRUCT = {
  active: false,
  applyToAllBills: false,
  code: '',
  discountPercent: '',
  discountAmount: '',
  id: null,
};

export default {
  name: 'PromoCodeSettings',
  directives: {
    mask,
  },
  components: {
    CouponIcon,
    TogglePanel,
    CapustaInput,
    HelpLink,
  },
  async created() {
    await this.loadPromoCodeLimits();
    this.initializePromoCode();
  },
  data() {
    return {
      limits: null,
      promoCode: DEFAULT_STRUCT,
      isShownDiscountPercent: true,
      promoCodeTokens: {
        mask: 'XXXXXXXXXXXX',
        tokens: {
          'X': {
            pattern: /[0-9a-zA-Zа-яА-ЯёЁ]/,
          },
        },
      },
      percentTokens: {
        mask: 'CCCC',
        tokens: {
          'C': {
            pattern: /[0-9/\\.]/,
          },
        },
      },
    };
  },
  computed: {
    isShownPromoCode() {
      return this.$validate.isNotNull(this.limits);
    },
    formattedDiscountAmount() {
      const replacedAmount = this.promoCode.discountAmount.replace(/\s/g, '');
      const preparedAmount = Math.round(replacedAmount * 10 ** this.currency.significant_zeros);

      return this.$validate.isPositiveNumber(preparedAmount) ? preparedAmount : null;
    },
    displayDiscountSymbol() {
      return this.isShownDiscountPercent ? '%' : this.currency.sign;
    },
    displayPlaceholderDiscountPercent() {
      return this.$t('promocode.limits.percent', {
        minDiscount: this.limits.minDiscount,
        maxDiscount: this.limits.maxDiscount,
      });
    },
    displayPlaceholderDiscountAmount() {
      return this.$t('promocode.limits.amount', {
        displayMinAmount: MIN_LIMIT_AMOUNT,
        displayMaxAmount: formatAmount(this.limits.amount, this.currency),
      });
    },
    isValidPromoCode() {
      if (this.$validate.isEmptyString(this.promoCode.code)) {
        return false;
      }

      return this.validatePromoCode();
    },
    isValidDiscountPercent() {
      if (!this.isShownDiscountPercent) {
        return false;
      }

      return this.validateDiscountPercent();
    },
    isValidDiscountAmount() {
      if (this.isShownDiscountPercent) {
        return false;
      }

      return this.validateDiscountAmount();
    },
    isValid() {
      if (!this.isValidPromoCode) {
        return false;
      }

      return this.isShownDiscountPercent
        ? this.isValidDiscountPercent : this.isValidDiscountAmount;
    },
    currency() {
      return this.PROJECT_INFO.currency;
    },
    ...mapState(['PROJECT_INFO']),
  },
  methods: {
    async loadPromoCodeLimits() {
      try {
        const { data } = await HTTPService.getBillPromoCodeLimits();
        this.limits = data;
      } catch (error) {
        console.error(error);
      }
    },
    initializePromoCode() {
      if (!this.$validate.isObjectWithFields(this.PROJECT_INFO, ['promoCode'])) {
        return;
      }

      if (this.$validate.isNull(this.PROJECT_INFO.promoCode)) {
        return;
      }

      this.promoCode = Object.assign({}, this.PROJECT_INFO.promoCode, {
        active: true,
        discountAmount: formatAmount(this.PROJECT_INFO.promoCode.discountAmount, {
          significant_zeros: this.currency.significant_zeros,
          type: this.currency.type,
        }),
      });

      this.isShownDiscountPercent = this.$validate.isPositiveNumber(this.promoCode.discountPercent);
    },
    validatePromoCode() {
      const { code } = this.promoCode;
      const lengthCode = code.length;

      return lengthCode >= 1 && lengthCode <= 12;
    },
    validateDiscountPercent() {
      const { discountPercent } = this.promoCode;
      const { minDiscount, maxDiscount } = this.limits;

      if (Number(discountPercent) < minDiscount) {
        return false;
      }

      return Number(discountPercent) <= Number(maxDiscount);
    },
    validateDiscountAmount() {
      const { discountAmount } = this.promoCode;

      if (Number(discountAmount) < MIN_LIMIT_AMOUNT) {
        return false;
      }

      return this.formattedDiscountAmount <= this.limits.amount;
    },
    onToggleInputType() {
      this.isShownDiscountPercent = !this.isShownDiscountPercent;

      if (this.isShownDiscountPercent) {
        this.promoCode.discountAmount = '';
      } else {
        this.promoCode.discountPercent = '';
      }
    },
    async onSubmit() {
      const payload = Object.assign({}, this.promoCode,
        this.isShownDiscountPercent && {
          discountPercent: Number(this.promoCode.discountPercent),
        },
        !this.isShownDiscountPercent && {
          discountAmount: this.formattedDiscountAmount,
        }
      );

      try {
        await HTTPService.sendBillPromoCode(payload);
        this.SET_PROMOCODE(payload);

        this.$alert({
          type: 'cabbage',
          text: 'promocode.active',
        });
      } catch (error) {
        console.error(error);
      }
    },
    async handleClickOnPanel() {
      this.promoCode.active = !this.promoCode.active;

      if (this.promoCode.active || this.$validate.isNull(this.promoCode.id)) {
        return;
      }

      try {
        await HTTPService.removePromoCodeById(this.promoCode.id);
        this.REMOVE_PROMOCODE();

        this.$alert({
          type: 'cabbage',
          text: 'promocode.inactive',
        });
      } catch (error) {
        console.error(error);
      }
    },
    ...mapMutations(['SET_PROMOCODE', 'REMOVE_PROMOCODE']),
  },
};
</script>

<style lang="sass" scoped>
  .input-container
    &:not(:last-child)
      margin-bottom: 20px

    .input
      margin: 0

  .switcher-container
    margin: 32px 0
    display: flex
    align-items: center
    justify-content: space-between

  .switcher-link-container
    i
      width: 22px
      height: 22px
      flex: none
      display: block
      background-image: url('~@/assets/svg/common/information.svg')
      background-position: center
      background-repeat: no-repeat
      background-size: auto

  .input-promo-code-container
    position: relative

  .input-control-button
    width: 80px
    height: 100%
    display: flex
    justify-content: center
    align-items: center
    font-weight: 500
    font-size: 20px
    line-height: 24px
    background-color: #131212
    border-radius: 0 8px 8px 0
    transition: background-color .2s
    cursor: pointer

    &:active
      background-color: var(--color-input-background)
</style>
